import { api } from '@/shared/lib/api';
import type {
  CurrentSubscription,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  InvoicesResponse,
  InvoicesQueryParams,
  PricingInfo,
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  TenantEntitlements,
  PlanChangeQuote,
  PlanChangeRequest,
  PlanChangeResult,
  CancelPlanChangeRequest,
  PlanCode,
  AiUnitsSummary,
  AiUnitLedgerQueryParams,
  AiUnitLedgerResponse,
  AiUnitPurchase,
  CreateAiUnitPurchaseRequest,
  CreateAiUnitPurchaseResult,
} from '../types/billing.types';

const BILLING = '/billing';
const PLAN_CHANGE = `${BILLING}/subscriptions/current/plan-change`;
const AI_UNITS = `${BILLING}/ai-units`;

/* ---- Pricing (public) ---- */

export async function getPricing(): Promise<PricingInfo> {
  const { data } = await api.get<PricingInfo>(`${BILLING}/pricing`);
  return data;
}

/* ---- Current subscription ---- */

export async function getCurrentSubscription(): Promise<CurrentSubscription> {
  const { data } = await api.get<CurrentSubscription>(
    `${BILLING}/subscriptions/current`,
  );
  return data;
}

/* ---- Create subscription ---- */

export async function createSubscription(
  request: CreateSubscriptionRequest,
): Promise<CreateSubscriptionResponse> {
  const { data } = await api.post<CreateSubscriptionResponse>(
    `${BILLING}/subscriptions`,
    request,
  );
  return data;
}

export async function cancelCurrentSubscription(
  request: CancelSubscriptionRequest,
): Promise<CancelSubscriptionResponse> {
  const { data } = await api.post<CancelSubscriptionResponse>(
    `${BILLING}/subscriptions/current/cancel`,
    { reason: request.reason },
    { headers: { 'Idempotency-Key': request.idempotencyKey } },
  );
  return data;
}

/* ---- Entitlements ---- */

export async function getEntitlements(): Promise<TenantEntitlements> {
  const { data } = await api.get<TenantEntitlements>(`${BILLING}/entitlements`);
  return data;
}

/* ---- Plan change (só o proprietário) ---- */

export async function getPlanChangeQuote(toPlanCode: PlanCode): Promise<PlanChangeQuote> {
  const { data } = await api.get<PlanChangeQuote>(`${PLAN_CHANGE}/quote`, {
    params: { toPlanCode },
  });
  return data;
}

/**
 * Pede a mudança. A mesma `Idempotency-Key` com o mesmo corpo devolve o estado
 * gravado, sem nova cobrança; por isso a chave fica estável durante a tentativa.
 * O 202 (downgrade com o gateway sem responder) volta como `failed_retryable`.
 */
export async function requestPlanChange(request: PlanChangeRequest): Promise<PlanChangeResult> {
  const body: { toPlanCode: PlanCode; paymentMethod?: string } = { toPlanCode: request.toPlanCode };
  if (request.paymentMethod) body.paymentMethod = request.paymentMethod;

  const { data } = await api.post<PlanChangeResult>(PLAN_CHANGE, body, {
    headers: { 'Idempotency-Key': request.idempotencyKey },
  });
  return data;
}

/** Desiste da mudança aberta (upgrade não pago ou downgrade agendado). */
export async function cancelPlanChange(request: CancelPlanChangeRequest): Promise<PlanChangeResult> {
  const { data } = await api.delete<PlanChangeResult>(PLAN_CHANGE, {
    headers: { 'Idempotency-Key': request.idempotencyKey },
  });
  return data;
}

/* ---- Invoices ---- */

export async function getInvoices(
  params: InvoicesQueryParams = {},
): Promise<InvoicesResponse> {
  const { data } = await api.get<InvoicesResponse>(`${BILLING}/invoices`, {
    params,
  });
  return data;
}

/* ---- Atendimentos do Atendente IA (change billing-ai-units) ---- */

/** Saldo, ciclo, lotes, previsão, aviso e estimativa da Meta (`billing.read`). */
export async function getAiUnits(): Promise<AiUnitsSummary> {
  const { data } = await api.get<AiUnitsSummary>(AI_UNITS);
  return data;
}

/** Extrato paginado, do mais recente para o mais antigo (`billing.read`). */
export async function getAiUnitEntries(
  params: AiUnitLedgerQueryParams = {},
): Promise<AiUnitLedgerResponse> {
  const { data } = await api.get<AiUnitLedgerResponse>(`${AI_UNITS}/entries`, { params });
  return data;
}

/**
 * Compra um pacote (`billing.write`). A API responde 201 na compra criada e
 * 200 na repetição da mesma `Idempotency-Key` (clique duplo, nova tentativa
 * depois de uma falha do gateway), sempre com a compra; `created` diz qual.
 */
export async function createAiUnitPurchase(
  request: CreateAiUnitPurchaseRequest,
): Promise<CreateAiUnitPurchaseResult> {
  const body: { packCode: string; paymentMethod: string; billingInfo?: CreateAiUnitPurchaseRequest['billingInfo'] } = {
    packCode: request.packCode,
    paymentMethod: request.paymentMethod,
  };
  if (request.billingInfo) body.billingInfo = request.billingInfo;

  const response = await api.post<AiUnitPurchase>(`${AI_UNITS}/purchases`, body, {
    headers: { 'Idempotency-Key': request.idempotencyKey },
  });
  return { purchase: response.data, created: response.status === 201 };
}

/** Uma compra do tenant (`billing.read`), relida até o pagamento chegar. */
export async function getAiUnitPurchase(purchaseId: string): Promise<AiUnitPurchase> {
  const { data } = await api.get<AiUnitPurchase>(
    `${AI_UNITS}/purchases/${encodeURIComponent(purchaseId)}`,
  );
  return data;
}
